import { canv } from "@/canv/global.js";
import { getTarget } from "@/canv/utils.js";
import type { widgetOption } from "@/widgetMenu/widgetMenu";
import { wmBase } from "@/widgetMenu/wmBase";

export class WmColor extends wmBase {
    constructor(options: widgetOption<string>[], settings: { isText?: boolean } = {}) {
        super();
        this.options = options;
        this.className = settings.isText ? "wm_color_text" : "wm_color";
        this.isText = !!settings.isText;
    }

    build() {
        const uniqValues: string[] = [];

        for (let i = 0; i < this.options.length; i++) {
            const value = this.options[i].value;
            if (!uniqValues.includes(value)) {
                uniqValues.push(value);
            }
        }

        const mainColor = uniqValues[0] ?? "white";
        const adderColor = uniqValues[1] ?? mainColor;

        this.currentColor = mainColor;

        if (this.isText) {
            return this.getWidgetIconTemplate(mainColor, adderColor);
        }

        return `
            <div class="wm_color" style="--main-color:${mainColor}; --adder-color:${adderColor}">
                <div class="wm_color_inner">
                    <div class="wm_color_inner_add"></div>
                </div>
            </div>
        `;
    }

    init() {
        const item = document.querySelector(`.${this.className}`);
        item?.removeEventListener("click", this.toggleSubMenu, false);
        item?.addEventListener("click", this.toggleSubMenu, false);
    }

    toggleSubMenu = (e: Event) => {
        const subm = document.querySelector(".wm_color_submenu");
        if (subm) {
            subm.outerHTML = "";
            return;
        }
        const otherSubm = document.querySelector(".widgetSubMenu");
        if (otherSubm) {
            otherSubm.outerHTML = "";
        }

        canv.widgetMenu.subMenuBoss = this.className;
        const submenu = document.createElement("div");
        submenu.classList.add("widgetSubMenu");
        submenu.classList.add("wm_color_submenu");
        submenu.style.opacity = "0";
        document.body.prepend(submenu);

        for (let i = 0; i < this.colors.length; i++) {
            submenu.innerHTML += this.submenuColorTemplate(this.colors[i], i);
        }

        const pallet = document.querySelector<HTMLInputElement>(".wm_color_submenu_item_pallete");
        pallet?.removeEventListener("input", this.colorPickerPick);
        pallet?.addEventListener("input", this.colorPickerPick);

        const items = document.querySelectorAll<HTMLElement>(".wm_color_submenu_item");
        for (let i = 0; i < items.length; i++) {
            items[i].removeEventListener("click", this.itemClick, false);
            items[i].addEventListener("click", this.itemClick, false);
        }

        setTimeout(canv.widgetMenu.TryLocateMenu.bind(canv.widgetMenu), 50);
    };

    colorPickerPick = (event: Event) => {
        const input = event.target as HTMLInputElement;
        if (!input) {
            return;
        }

        const menuItem = document.querySelector(`.${this.className}`);
        menuItem?.setAttribute(
            "style",
            `--main-color: ${input.value}; --adder-color: ${input.value}`,
        );

        // debounce apply value:
        if (this.applyValueTimeout) {
            clearTimeout(this.applyValueTimeout);
        }
        this.applyValueTimeout = setTimeout(() => {
            this.applyValueTimeout = null;
            this.applyValue(this.options, input.value, false, true);
        }, 100);
    };

    itemClick = (e: Event) => {
        const item = getTarget(e, "wm_color_submenu_item");
        const numAttr = item.getAttribute("num");
        if (!numAttr) return;
        const num = Number.parseInt(numAttr, 10);
        if (Number.isNaN(num)) return;

        let value = this.colors[num];
        let menuValue = this.colors[num];
        if (num === 17) {
            return;
        }

        if (num === 0) {
            value = "transparent";
            menuValue = "white";
        }

        this.applyValue(this.options, value, true, true);

        const menuItem = document.querySelector<HTMLElement>(`.${this.className}`);
        menuItem?.setAttribute("style", `--main-color: ${menuValue}; --adder-color: ${menuValue}`);
    };

    getWidgetIconTemplate = (mainColor: string, adderColor: string) => {
        return `
            <div class="wm_color_text" style="--main-color:${mainColor}; --adder-color:${adderColor}">
                <div class="wm_color_text_letter">A</div>
                <div class="wm_color_text_underline"></div>
            </div>
        `;
    };

    submenuColorTemplate(color: string, num: number) {
        let ifInp = "";
        if (num === 17) {
            ifInp = `<input type="color" class="wm_color_submenu_item_pallete" value="${this.currentColor}">`;
        }

        if (num === 0) {
            return `
                <div class="wm_color_submenu_item" num=${num}>
                    <div class="wm_color_submenu_item_no">
                        <div></div>
                    </div>
                </div>
            `;
        }

        return `
            <div class="wm_color_submenu_item" num=${num}>
                ${ifInp}
                <div style="background: ${color}"></div>
            </div>

        `;
    }

    applyValueTimeout: ReturnType<typeof setTimeout> | null = null;

    name = "wm_color";
    className = "wm_color";
    isText = false;
    options: widgetOption<string>[] = [];
    currentColor = "white";
    colors: string[] = [
        "#FFFFFF",
        "#111111",
        "#E03E1A",
        "#f79722",
        "#f3c11b",
        "#198f51",
        "#0c8de9",
        "#8a38f5",
        "#ffffff",
        "#383838",
        "#d9d9d9",
        "#fbbcb6",
        "#ffc470",
        "#f9df90",
        "#a1e8b9",
        "#a8d7fa",
        "#d6b6fb",
        `radial-gradient(50% 50% at 50% 50%,#ffffff 0%,transparent 100%),conic-gradient(from
            0deg at 50% 50%,#ff0000
            0deg,#ffa800
            47.73deg,#ffff00
            79.56deg,#00ff00
            121.33deg,#00ffff
            180.99deg,#0000ff
            238.67deg,#ff00ff
            294.36deg,#ff0000
            360deg),#c4c4c4;`,
    ];
}
